<template>
  <div class="container-xxl lesson-generation-home" id="lesson-generation">
    <PhaseComponent
      :selectedNumber="generatePhase"
      @phase-clicked="handlePhaseClick"
    ></PhaseComponent>

    <PromptForm
      ref="promptFormRef"
      :disableButton="generateButtonDisabled"
      @generate-clicked="generateLessonPackage"
    >
    </PromptForm>
    <div class="generation-result-container">
      <GenerateSection
        :showPromptPreview="showPromptPreview"
        :showErrorSection="showErrorGenerateSection"
        :showLoading="showLoadingGenerateSection"
        :showResultSection="showResultGenerateSection"
        @generate-clicked="generateLessonPackage"
      >
      </GenerateSection>
    </div>
  </div>

  <LoginRequiredModal
    v-model="showLoginRequiredModal"
    :redirect-path="computedRedirectPath"
    @close="showLoginRequiredModal = false"
  />
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { onMounted, ref, defineEmits, computed, watch } from "vue";

import GenerateSection from "@/components/GenerateSection.vue";
import PhaseComponent from "@/components/PhaseComponent.vue";
import PromptForm from "@/components/PromptForm.vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
// import { useRouter } from 'vue-router';
// eslint-disable-next-line
import LessonPackageService from "@/services/lessonPackageService";
import LocalStorageService from "@/services/localStorageService";
import LoginRequiredModal from "@/components/modal/LoginRequiredModal.vue";

const emit = defineEmits(["updateHeroSection"]);
const store = useStore();
const route = useRoute();
// const router = useRouter();

const user = computed(() => store.state.user);

const generatePhase = ref(1);
const showGenerateSection = ref(false);
const showHeroSection = ref(true);
const promptFormRef = ref(null);
const generateButtonDisabled = ref(false);
const showLoginRequiredModal = ref(false);

const showLoadingGenerateSection = ref(false);
const showResultGenerateSection = ref(true);
const showPromptPreview = ref(false);
const showErrorGenerateSection = ref(false);
const autogenTriggered = ref(false);

const computedRedirectPath = computed(() => {
  try {
    const basePath = route.path || "/";
    const url = new URL(window.location.origin + basePath);
    const query = { ...route.query };
    // Embed an explicit autoCart flag in the redirect URL
    url.search = new URLSearchParams({
      ...query,
      autogen: "1",
      key: "step-2",
    }).toString();
    return url.pathname + url.search;
  } catch (e) {
    return route.fullPath || route.path || "/";
  }
});

onMounted(async () => {
  localStorage.removeItem("processAuto");
  generateButtonDisabled.value = false;
  setGeneratePhase(1);

  const receivedValue = route.query.key;

  if (receivedValue === "reset") {
    resetState();
  } else if (receivedValue === "step-2") {
    const formData = LocalStorageService.getFormData();
    const formResults = LocalStorageService.getFormResults();

    if (formData) {
      store.commit("SET_FORM_DATA", formData);
      if (formResults) {
        store.commit("SET_FORM_RESULTS", formResults);
      }
    } else {
      resetState();
      return;
    }

    setGeneratePhase(2);
    showErrorGenerateSection.value = false;
    showGenerateSection.value = true;
    updateHeroSection(false);
    showPromptPreview.value = true;
  } else {
    const savedFormData = LocalStorageService.getFormData();
    if (!savedFormData || Object.keys(savedFormData).length === 0) {
      resetState();
    }
  }

  tryAutoGenerateFromLogin();

  let cacheGenerate = await LocalStorageService.getCache("cache-generate");

  if (cacheGenerate) {
    LocalStorageService.setFormResults(cacheGenerate.formResult);
    store.commit("SET_FORM_RESULTS", cacheGenerate.formResult);
    LocalStorageService.setFormData(cacheGenerate.formData);
    store.commit("SET_FORM_DATA", cacheGenerate.formData);

    console.log("cacheGenerate.formData", cacheGenerate.formData);

    setGeneratePhase(2);
    showErrorGenerateSection.value = false;
    showGenerateSection.value = true;
    updateHeroSection(false);
    showPromptPreview.value = true;
  }
});

// eslint-disable-next-line
const generateLessonPackage = async (data) => {
  if (!user.value) {
    store.commit("SET_FORM_DATA", data);
    try {
      LocalStorageService.setFormData(data);
    } catch (e) {
      // no-op
    }
    showLoadingGenerateSection.value = false;
    showLoginRequiredModal.value = true;
    return;
  }

  try {
    setGeneratePhase(2);
    showErrorGenerateSection.value = false;
    showGenerateSection.value = true;
    updateHeroSection(false);
    generateButtonDisabled.value = true;

    showPromptPreview.value = true;

    // eslint-disable-next-line
    LocalStorageService.setFormResults([]);
    store.commit("SET_FORM_RESULTS", []);

    showLoadingGenerateSection.value = true;
    let response = await LessonPackageService.generateLessonPackage(
      user.value.email,
      data
    );
    if (response.status === 200) {
      showLoadingGenerateSection.value = false;
      data.resource_id = response.data.data.id;

      LocalStorageService.setFormResults(response.data.data);
      store.commit("SET_FORM_RESULTS", response.data.data);
    } else {
      throw new Error(`First request failed with status ${response.status}`);
    }

    await new Promise((resolve) => setTimeout(resolve, 5000)); // Delay for 5 seconds

    showLoadingGenerateSection.value = true;
    response = await LessonPackageService.generateLessonPackage(
      user.value.email,
      data
    );
    if (response.status === 200) {
      showLoadingGenerateSection.value = false;

      LocalStorageService.setFormResults(response.data.data);
      store.commit("SET_FORM_RESULTS", response.data.data);
    } else {
      throw new Error(`Second request failed with status ${response.status}`);
    }

    await new Promise((resolve) => setTimeout(resolve, 5000)); // Delay for 5 seconds

    showLoadingGenerateSection.value = true;
    response = await LessonPackageService.generateLessonPackage(
      user.value.email,
      data
    );
    if (response.status === 200) {
      showLoadingGenerateSection.value = false;

      LocalStorageService.setFormResults(response.data.data);
      store.commit("SET_FORM_RESULTS", response.data.data);
    } else {
      throw new Error(`Third request failed with status ${response.status}`);
    }

    generateButtonDisabled.value = false;
  } catch (error) {
    console.error("Error generating lesson package:", error);
    generateButtonDisabled.value = false;
    showLoadingGenerateSection.value = false;
    showErrorGenerateSection.value = true;
  }
};

const handlePhaseClick = (phaseNumber) => {
  console.log("phaseNumber", phaseNumber);
  if (phaseNumber === 1) {
    resetState(true); // Preserve form data when going back to phase 1
  }
};

const tryAutoGenerateFromLogin = () => {
  if (autogenTriggered.value) return;
  const shouldAutogen =
    route.query.autogen === "1" || route.query.autogen === 1;
  const hasUser = !!user.value;
  let formData = store.state.formData;

  if (!formData || Object.keys(formData || {}).length === 0) {
    try {
      const fromLocal = LocalStorageService.getFormData
        ? LocalStorageService.getFormData()
        : null;
      if (fromLocal && Object.keys(fromLocal).length > 0) {
        store.commit("SET_FORM_DATA", fromLocal);
        formData = fromLocal;
      }
    } catch (e) {
      // no-op
      console.log("error", e);
    }
  }

  if (
    shouldAutogen &&
    hasUser &&
    formData &&
    Object.keys(formData).length > 0
  ) {
    autogenTriggered.value = true;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("autogen");
      window.history.replaceState({}, "", url.toString());
    } catch (e) {
      // no-op
    }
    generateLessonPackage(formData);
  }
};

watch(
  () => user.value,
  () => {
    tryAutoGenerateFromLogin();
  }
);

watch(
  () => store.state.formData,
  () => {
    tryAutoGenerateFromLogin();
  }
);

watch(
  () => route.query.autogen,
  () => {
    tryAutoGenerateFromLogin();
  }
);

const setGeneratePhase = (newPhase) => {
  generatePhase.value = newPhase;
};

const updateHeroSection = (value) => {
  showHeroSection.value = value;
  emit("updateHeroSection", value);
};

const resetState = async (preserveFormData = false) => {
  showErrorGenerateSection.value = false;
  generatePhase.value = 1;
  updateHeroSection(true);
  showLoadingGenerateSection.value = false;
  showPromptPreview.value = false;

  if (promptFormRef.value && promptFormRef.value.resetForm) {
    promptFormRef.value.resetForm();
  }

  // Only reset form data if preserveFormData is false
  if (!preserveFormData) {
    LocalStorageService.resetFormData();
    LocalStorageService.resetFormResults();
    store.commit("SET_FORM_RESULTS", []);
  }

  if (window.location.search) {
    const url = new URL(window.location);

    url.search = "";

    window.history.pushState({}, "", url);
  }
};
</script>

<style scoped>
.lesson-generation-container {
  margin-top: 16px;
}

.lesson-generation-home {
  max-width: 973px;
  margin: 0 auto;
}

.generation-result-container {
  margin: 24px 0px;
}

@media (max-width: 1300px) {
  .lesson-generation-home {
    padding: 0px 150px;
  }
}

@media (max-width: 960px) {
  .lesson-generation-home {
    padding: 0px 10px;
  }
}

@media (max-width: 480px) {
  .lesson-generation-container {
    padding: 1rem 1.5rem;
    max-width: 100%;
  }

  .generation-result-container {
    padding: 0.5rem;
  }

  .lesson-generation-home {
    padding: 0px 10px;
  }
}

@media (min-width: 481px) and (max-width: 767px) {
  .lesson-generation-container {
    padding: 1.2rem;
  }
}
</style>
